/** Guided channel set-up screens (ADR-0064): widgets, connections, notifications, settings. */
export default {
  en: {
    copied: "Copied",
    cancel: "Cancel",
    ok: "Yes",
    next: "Next",
    back: "Back",
    finish: "Finish",
    close: "Close",
    technical: "Technical details",
    lang: { ar: "Arabic", en: "English" },
    widgets: {
      intro: "A chat button for your website. Visitors talk to the agent you choose; when they ask for a person, the conversation comes to the desk.",
      add: "Add a website chat",
      none: "No website chat yet. Add one and copy the code to your website.",
      on: "On",
      off: "Turned off",
      getCode: "Get the code",
      edit: "Edit",
      editAria: "Edit the website chat {{name}}",
      rotate: "New code",
      rotateTitle: "Make a new code?",
      rotateBody: "The code on your website stops working at once. Put the new code on your website straight away.",
      rotateAria: "Make a new code for {{name}}",
      rotated: "New code ready for {{name}}. Put it on your website.",
      turnOff: "Turn off",
      turnOn: "Turn on",
      turnOffTitle: "Turn this chat off? The chat button stops working on your website.",
      turnOnTitle: "Turn this chat back on?",
      turnOffAria: "Turn off {{name}}",
      turnOnAria: "Turn on {{name}}",
      turnedOff: "{{name}} is turned off",
      turnedOn: "{{name}} is on again",
      websites: "Websites",
      websitesHelp: "Only these websites can show the chat. Add each address your visitors use.",
      addSite: "Add",
      siteHelp: "Type your website address, for example www.example.com",
      siteWillBe: "Will be added as {{origin}}",
      siteInvalid: "This does not look like a website address",
      removeSite: "Remove {{site}}",
      agent: "Agent",
      agentHelp: "The agent that answers your visitors.",
      agentPlaceholder: "Choose an agent",
      language: "Language",
      name: "Name",
      nameHelp: "For you only, for example “Support website”.",
      colour: "Colour",
      colourHelp: "Your brand colour. Text colours adjust automatically so they stay readable.",
      suggested: "Suggested colours",
      useColour: "Use colour {{colour}}",
      cornerLabel: "Corner",
      corner: { right: "Bottom right", left: "Bottom left" },
      preview: "Live preview",
      previewHelp: "This is the real chat window with sample messages. Nothing is sent.",
      previewFrame: "Live preview of the website chat",
      addTitle: "Add a website chat",
      editTitle: "Edit {{name}}",
      create: "Create and get the code",
      save: "Save changes",
      created: "{{name}} is ready. Copy the code to your website.",
      saved: "{{name}} saved",
    },
    install: {
      title: "Put {{name}} on your website",
      step1: "Copy this code.",
      copy: "Copy the code",
      copyAria: "Copy the code of {{name}}",
      step2: "Paste it into your website, just before the closing </body> tag, on every page that should show the chat.",
      step2Help: "Most website builders have a place for “custom code” or “footer scripts”. If someone else runs your website, send them the instructions below.",
      step3: "Open your website ({{sites}}) and click the chat button to try it.",
      step3Help: "No button? Check that the website address matches exactly and that the chat is turned on.",
      teamTitle: "Someone else runs your website?",
      teamHelp: "Copy the instructions and send them to your web team.",
      copyTeam: "Copy instructions for the web team",
      handoff: "Please add our website chat.\n1. Paste this code just before </body> on every page: {{snippet}}\n2. It only works on: {{sites}}\n3. Reload the page and check that the chat button appears.",
    },
    connect: {
      intro: "Let customers reach your agents from the apps they already use. Passwords and secret keys are stored in the platform's vault and are never shown again.",
      vendor: { whatsapp: "WhatsApp", teams: "Microsoft Teams", slack: "Slack", telegram: "Telegram", mattermost: "Mattermost", other: "Other chat app", email: "E-mail" },
      defaultName: { whatsapp: "WhatsApp", teams: "Teams", slack: "Slack", telegram: "Telegram", mattermost: "Mattermost", other: "Chat app" },
      emailBlurb: "Answer e-mails sent to your support address.",
      mattermostBlurb: "Built in: connect with two webhooks.",
      bridgeBlurb: "Connects through a small bridge your IT team runs.",
      connect: "Connect",
      change: "Change",
      connectAria: "Connect {{app}}",
      connected: "Connected",
      connectedTitle: "Connected chat apps",
      none: "No chat app connected yet.",
      colName: "Name",
      colApp: "App",
      colAgent: "Agent",
      colStatus: "Status",
      colActions: "Actions",
      on: "On",
      off: "Turned off",
      test: "Send a test",
      testAria: "Send a test message through {{name}}",
      changeAria: "Change {{name}}",
      turnOff: "Turn off",
      turnOn: "Turn on",
      turnOffTitle: "Turn this connection off? Messages from this app are no longer answered.",
      turnOnTitle: "Turn this connection back on?",
      turnedOff: "{{name}} is turned off",
      turnedOn: "{{name}} is on again",
      wizardTitle: "Connect {{app}}",
      changeTitle: "Change {{name}}",
      stepPrepare: "Prepare",
      stepDetails: "Details",
      stepTest: "Test",
      mmIntro: "In Mattermost, an administrator creates two webhooks: one that sends messages to us and one we use to reply.",
      mm1: "Create an outgoing webhook that posts to the address below. Copy the token Mattermost shows.",
      mm2: "Create an incoming webhook for the channel the agent should answer in. Copy its address.",
      mm3: "Click Next and paste both.",
      bridgeIntro: "{{app}} connects through a small bridge program your IT team runs inside your network. It checks {{app}}'s own security and passes messages to and from the platform.",
      br1: "Give your IT team the address below: the bridge sends incoming messages there.",
      br2: "Give them the shared secret from the next step: the bridge signs every message with it.",
      br3: "Ask them for the bridge's sending address (where the platform sends replies).",
      inboundLabel: "Address for incoming messages",
      copyInbound: "Copy the address for incoming messages",
      inboundHelp: "Only your bridge or chat server needs this address.",
      displayName: "Name",
      displayNameHelp: "How this connection appears here, for example “Support WhatsApp”.",
      agent: "Agent that answers",
      agentPlaceholder: "Choose an agent",
      mmToken: "Outgoing webhook token",
      mmTokenHelp: "Shown by Mattermost when you create the outgoing webhook.",
      sharedSecret: "Shared secret",
      sharedSecretHelp: "Generated for you. Copy it now and give it to your IT team: it is not shown again after saving.",
      keepSecret: "Leave empty to keep the current one.",
      generate: "Generate",
      copySecret: "Copy the secret",
      mmWebhook: "Incoming webhook address",
      mmWebhookHelp: "The address Mattermost shows for the incoming webhook.",
      endpoint: "Bridge sending address",
      endpointHelp: "Where the platform sends replies for {{app}}. Your IT team gives you this.",
      technical: "Internal name: {{name}} · incoming path: {{path}}",
      saveConnect: "Save and connect",
      saved: "{{name}} connected",
      done: "{{name}} is connected",
      doneHelp: "Send a test message to check that replies arrive.",
      testTitle: "Send a test message",
      testFor: "Test {{name}}",
      conversation: "Chat or channel to send the test to",
      conversationHelp: "The ID of a {{app}} chat you can see, for example your own chat with the bot.",
      mmTestHelp: "The test message goes to the channel of the incoming webhook.",
      testLanguage: "Language of the test message",
      sendTest: "Send the test",
      testSent: "Test message sent",
      testSentHelp: "Check the chat: the message should be there.",
      testNotSent: "The test message was not sent",
      reason: {
        no_outbound: "There is no address for sending replies. Add it with Change.",
        conversation_required: "Enter the chat or channel to send the test to.",
        not_found: "This connection no longer exists.",
        disabled: "The connection is turned off. Turn it on first.",
        secret_missing: "The saved webhook address could not be found. Enter it again with Change.",
        upstream_failed: "The chat app or bridge did not accept the message. Check the sending address and that the bridge is running.",
      },
      emailTitle: "Connect e-mail",
      emailChangeTitle: "Change the e-mail connection",
      emailConfigured: "E-mail is connected. Saving a new secret replaces the old one: update your mail server too.",
      emailIntro: "Your mail server forwards the e-mails that arrive at your support address to the platform, signed with a shared secret. Replies go out through the platform's e-mail service.",
      email1: "In your mail server, forward incoming e-mails to this address:",
      email2: "Use this shared secret to sign them (copy it now, it is not shown again):",
      email3: "Save here, then send a test e-mail to your support address.",
      emailNote1: "Automatic replies and mailing lists are ignored.",
      emailNote2: "E-mails are treated as untrusted: the agent never follows instructions written in them.",
      emailConfirm: "I have copied the secret into the mail server",
      emailSave: "Save the e-mail connection",
      emailSaved: "E-mail connected",
    },
    notify: {
      intro: "Send one message to a customer. It is only sent if the customer agreed to receive this kind of message, outside quiet hours and within the limits.",
      how: "Send by",
      via: { email: "E-mail", chat: "Chat app" },
      connector: "Chat app",
      noConnector: "No chat app connected",
      toEmail: "Customer's e-mail address",
      toChat: "Customer's chat ID",
      purpose: "Kind of message",
      purposeHelp: "The customer must have agreed to this kind of message.",
      message: "Message",
      templates: { appointment_reminder: "Appointment reminder", case_update: "Update on a request", generic_notice: "Free text notice", payment_due: "Payment reminder" },
      params: { date: "Date", reference: "Reference", time: "Time", status: "New status", amount: "Amount", currency: "Currency", message: "Your message" },
      language: "Language of the message",
      send: "Send",
      previewTitle: "What the customer will read",
      previewEmpty: "Choose a message to see it here.",
      status: { sent: "Sent to {{to}}", deferred: "Will be sent to {{to}} later", blocked: "Not sent to {{to}}" },
      when: "Planned for {{at}}.",
      reason: {
        no_consent: "The customer has not agreed to this kind of message. Record their consent first (Consents tab).",
        opted_out: "The customer asked not to be contacted on this channel.",
        quiet_hours: "It is quiet hours or the weekend; it goes out when they end.",
        rate_limited: "Too many messages were sent recently; it goes out within the hour.",
        not_allowed: "Your account may not send notifications.",
      },
    },
    settings: {
      intro: "These settings apply to every channel of your organisation. Only channel administrators can change them.",
      readOnly: "You can read these settings. Changing them needs the channel administrator role.",
      hoursTitle: "Working hours",
      useOrg: "Use the organisation's time zone and weekend",
      useOrgOn: "Following the organisation: {{zone}}.",
      useOrgNone: "The organisation has no time zone set yet; the channel's own values are used.",
      useOrgOff: "This channel has its own time zone and weekend.",
      timezone: "Time zone",
      quietStart: "Quiet hours start",
      quietEnd: "Quiet hours end",
      quietHelp: "No notifications are sent between these times.",
      weekendDays: "Weekend (no notifications)",
      weekendOwn: "The organisation does not set a weekend; choose it here.",
      days: { "1": "Monday", "2": "Tuesday", "3": "Wednesday", "4": "Thursday", "5": "Friday", "6": "Saturday", "7": "Sunday" },
      peopleTitle: "When a customer asks for a person",
      queueRoles: "Who answers",
      queueHelp: "People with any of these roles see the conversations on the desk.",
      sla: "Answer within",
      slaHelp: "After this time the request is shown as late.",
      minutes: "minutes",
      defaultAgent: "Agent that answers by default",
      noAgent: "None",
      messagesTitle: "Messages",
      defaultLanguage: "Default language",
      digits: "Numbers in messages",
      digitsLatin: "0 1 2 3",
      digitsArabic: "٠ ١ ٢ ٣",
      perHour: "Most notifications per hour",
      perRecipient: "Most notifications per customer per day",
      optOut: "Words that stop messages",
      optOutHelp: "A customer who sends one of these words is not contacted again on that channel. STOP and إيقاف always work.",
      purposesTitle: "Kinds of messages customers can agree to",
      purposesHelp: "Used when you record a customer's consent and when you send a notification.",
      purposeAr: "Kind of message {{n}} in Arabic",
      purposeEn: "Kind of message {{n}} in English",
      purposeArPh: "بالعربية",
      purposeEnPh: "In English",
      removePurpose: "Remove {{name}}",
      newPurposeAr: "New kind of message in Arabic",
      newPurposeEn: "New kind of message in English",
      addPurpose: "Add",
      save: "Save settings",
      saved: "Channel settings saved",
    },
    consents: {
      intro: "A customer must agree before you send them messages. Look them up by how you reach them; addresses are stored scrambled and shown here partly hidden.",
      recipient: "Customer",
      purposeHelp: "The kinds of messages come from the channel settings.",
    },
    roleNames: {
      approver: "Approvers",
      reviewer: "Reviewers",
      "case-worker": "Case workers",
      "case-supervisor": "Case supervisors",
      finance: "Finance team",
      "finance-approver": "Finance approvers",
      builder: "Agent builders",
      "tool-owner": "Tool owners",
      "retrieval-curator": "Knowledge curators",
      "eval-curator": "Evaluation curators",
      "memory-curator": "Memory curators",
      "data-steward": "Data stewards",
      "release-approver": "Release approvers",
      "agent-admin": "Agent administrators",
      "model-admin": "Model administrators",
      "guardrails-admin": "Guardrails administrators",
      "case-admin": "Case administrators",
      "privacy-officer": "Privacy officers",
      "pii-reader": "Personal data readers",
      "kill-switch-operator": "Emergency stop operators",
      "tenant-admin": "Organisation administrators",
      auditor: "Auditors",
      operator: "Operators",
      executive: "Executives",
      "channel-agent": "Support agents",
      "channel-supervisor": "Support supervisors",
      "channel-admin": "Channel administrators",
      "channel-consent-admin": "Consent administrators",
      "channel-campaigner": "Notification senders",
    },
    issues: {
      name_required: "Enter a name",
      origins_required: "Add at least one website",
      agent_required: "Choose an agent",
      secret_short: "The secret needs at least 8 characters",
      webhook_required: "Enter the incoming webhook address",
      endpoint_required: "Enter the bridge sending address",
      url_invalid: "Addresses start with https:// or http://",
      email_secret_short: "The secret needs at least 16 characters",
      address_short: "Enter the customer's address",
      connector_required: "Choose the chat app",
      purpose_required: "Choose the kind of message",
      template_required: "Choose a message",
      fields_required: "Fill in every field of the message",
      time_invalid: "Choose both quiet-hours times",
      queue_required: "Choose who answers",
      purpose_label_required: "Every kind of message needs an Arabic and an English name",
      purpose_invalid: "A kind of message has an invalid internal key",
      purpose_duplicate: "Two kinds of message have the same name",
    },
    // Stream C (ADR-0101): website visitors get answers; the widget follows the released version.
    visitors: {
      ok: "Visitors get answers.",
      unknown: "Couldn't check whether visitors get answers.",
      title: "Visitors get no answer yet",
      intro: "Until this is fixed, visitors are told the assistant can't answer yet.",
      problem: {
        not_found: "The agent it points at no longer exists. Choose another agent.",
        not_released: "No version of the agent is released yet.",
        no_trigger_key: "The agent has no key for conversations without a signed-in person.",
        not_autonomous: "The agent may not work on its own, which it must when nobody is signed in.",
        channel_off: "The agent doesn't answer on websites.",
        tier_too_high: "The agent's risk level is too high to work without a signed-in person. Someone who builds agents must lower it first.",
      },
      allow: "Allow website visitors",
      allowAria: "Allow website visitors for {{name}}",
      confirmTitle: "Allow website visitors?",
      confirmIntro: "This sets up {{agent}} so visitors of this website chat get answers:",
      step: {
        key: "Create the key for conversations without a signed-in person.",
        version: "Save a new version that works on its own and answers on websites.",
        release: "Release it to Development.",
        follow: "Let this website chat follow the released version.",
      },
      confirmNote: "Each change is recorded in the activity log.",
      confirmOk: "Allow visitors",
      done: "Website visitors of {{name}} now get answers.",
      waiting: "Saved. The release waits for a sign-off in Governance.",
      failed: "Couldn't finish: {{message}}",
      askBuilder: "Ask someone who builds agents to allow website visitors for this agent (Builder › When it runs).",
      follows: "Follows the released version",
      followsNow: "Answers with the released version (now version {{version}}).",
      keeps: "Keeps version {{version}}",
      keepSwitch: "Keep one version",
      keepHelp: "Normally the chat answers with each new release. Keep one version only to hold it back.",
    },
  },
  ar: {
    copied: "تم النسخ",
    cancel: "إلغاء",
    ok: "نعم",
    next: "التالي",
    back: "رجوع",
    finish: "إنهاء",
    close: "إغلاق",
    technical: "تفاصيل تقنية",
    lang: { ar: "العربية", en: "الإنجليزية" },
    widgets: {
      intro: "زر محادثة لموقعك. يتحدث الزوار مع الوكيل الذي تختاره، وعندما يطلبون موظفاً تصل المحادثة إلى مكتب الموظفين.",
      add: "إضافة محادثة للموقع",
      none: "لا توجد محادثة للموقع بعد. أضف واحدة وانسخ الرمز إلى موقعك.",
      on: "مفعّلة",
      off: "متوقفة",
      getCode: "الحصول على الرمز",
      edit: "تعديل",
      editAria: "تعديل محادثة الموقع {{name}}",
      rotate: "رمز جديد",
      rotateTitle: "إنشاء رمز جديد؟",
      rotateBody: "يتوقف الرمز الموجود على موقعك فوراً. ضع الرمز الجديد على موقعك مباشرة.",
      rotateAria: "إنشاء رمز جديد لـ {{name}}",
      rotated: "الرمز الجديد جاهز لـ {{name}}. ضعه على موقعك.",
      turnOff: "إيقاف",
      turnOn: "تشغيل",
      turnOffTitle: "إيقاف هذه المحادثة؟ سيتوقف زر المحادثة على موقعك.",
      turnOnTitle: "إعادة تشغيل هذه المحادثة؟",
      turnOffAria: "إيقاف {{name}}",
      turnOnAria: "تشغيل {{name}}",
      turnedOff: "تم إيقاف {{name}}",
      turnedOn: "عادت {{name}} للعمل",
      websites: "المواقع",
      websitesHelp: "تظهر المحادثة على هذه المواقع فقط. أضف كل عنوان يستخدمه زوارك.",
      addSite: "إضافة",
      siteHelp: "اكتب عنوان موقعك، مثلاً www.example.com",
      siteWillBe: "ستتم إضافته بالصيغة {{origin}}",
      siteInvalid: "هذا لا يبدو عنوان موقع",
      removeSite: "إزالة {{site}}",
      agent: "الوكيل",
      agentHelp: "الوكيل الذي يجيب زوارك.",
      agentPlaceholder: "اختر وكيلاً",
      language: "اللغة",
      name: "الاسم",
      nameHelp: "لك أنت فقط، مثلاً «موقع الدعم».",
      colour: "اللون",
      colourHelp: "لون علامتك التجارية. تتغير ألوان النص تلقائياً لتبقى مقروءة.",
      suggested: "ألوان مقترحة",
      useColour: "استخدام اللون {{colour}}",
      cornerLabel: "الزاوية",
      corner: { right: "أسفل اليمين", left: "أسفل اليسار" },
      preview: "معاينة مباشرة",
      previewHelp: "هذه نافذة المحادثة الحقيقية مع رسائل تجريبية. لا يُرسل شيء.",
      previewFrame: "معاينة مباشرة لمحادثة الموقع",
      addTitle: "إضافة محادثة للموقع",
      editTitle: "تعديل {{name}}",
      create: "إنشاء والحصول على الرمز",
      save: "حفظ التغييرات",
      created: "{{name}} جاهزة. انسخ الرمز إلى موقعك.",
      saved: "تم حفظ {{name}}",
    },
    install: {
      title: "ضع {{name}} على موقعك",
      step1: "انسخ هذا الرمز.",
      copy: "نسخ الرمز",
      copyAria: "نسخ رمز {{name}}",
      step2: "الصقه في موقعك قبل الوسم ‎</body>‎ مباشرة، في كل صفحة تريد أن تظهر فيها المحادثة.",
      step2Help: "معظم أدوات بناء المواقع فيها مكان لـ«رمز مخصص» أو «نصوص التذييل». إذا كان شخص آخر يدير موقعك فأرسل له التعليمات أدناه.",
      step3: "افتح موقعك ({{sites}}) واضغط زر المحادثة لتجربته.",
      step3Help: "لا يظهر الزر؟ تأكد أن عنوان الموقع مطابق تماماً وأن المحادثة مفعّلة.",
      teamTitle: "شخص آخر يدير موقعك؟",
      teamHelp: "انسخ التعليمات وأرسلها إلى فريق الموقع.",
      copyTeam: "نسخ التعليمات لفريق الموقع",
      handoff: "يرجى إضافة محادثة موقعنا.\n١. الصق هذا الرمز قبل ‎</body>‎ في كل صفحة: {{snippet}}\n٢. يعمل فقط على: {{sites}}\n٣. أعد تحميل الصفحة وتأكد من ظهور زر المحادثة.",
    },
    connect: {
      intro: "دع العملاء يصلون إلى وكلائك من التطبيقات التي يستخدمونها. تُحفظ كلمات المرور والمفاتيح السرية في خزنة المنصة ولا تُعرض مرة أخرى.",
      vendor: { whatsapp: "واتساب", teams: "مايكروسوفت تيمز", slack: "سلاك", telegram: "تيليجرام", mattermost: "ماترموست", other: "تطبيق محادثة آخر", email: "البريد الإلكتروني" },
      defaultName: { whatsapp: "واتساب", teams: "تيمز", slack: "سلاك", telegram: "تيليجرام", mattermost: "ماترموست", other: "تطبيق محادثة" },
      emailBlurb: "الرد على الرسائل المرسلة إلى بريد الدعم.",
      mattermostBlurb: "مدمج: يتصل عبر خطافين (webhooks).",
      bridgeBlurb: "يتصل عبر جسر صغير يشغّله فريق تقنية المعلومات.",
      connect: "ربط",
      change: "تغيير",
      connectAria: "ربط {{app}}",
      connected: "متصل",
      connectedTitle: "تطبيقات المحادثة المتصلة",
      none: "لا يوجد تطبيق محادثة متصل بعد.",
      colName: "الاسم",
      colApp: "التطبيق",
      colAgent: "الوكيل",
      colStatus: "الحالة",
      colActions: "الإجراءات",
      on: "مفعّل",
      off: "متوقف",
      test: "إرسال تجربة",
      testAria: "إرسال رسالة تجريبية عبر {{name}}",
      changeAria: "تغيير {{name}}",
      turnOff: "إيقاف",
      turnOn: "تشغيل",
      turnOffTitle: "إيقاف هذا الربط؟ لن يُرد على رسائل هذا التطبيق.",
      turnOnTitle: "إعادة تشغيل هذا الربط؟",
      turnedOff: "تم إيقاف {{name}}",
      turnedOn: "عاد {{name}} للعمل",
      wizardTitle: "ربط {{app}}",
      changeTitle: "تغيير {{name}}",
      stepPrepare: "التحضير",
      stepDetails: "التفاصيل",
      stepTest: "التجربة",
      mmIntro: "في ماترموست ينشئ المسؤول خطافين: واحد يرسل الرسائل إلينا وآخر نستخدمه للرد.",
      mm1: "أنشئ خطافاً صادراً (outgoing webhook) يرسل إلى العنوان أدناه، وانسخ الرمز الذي يعرضه ماترموست.",
      mm2: "أنشئ خطافاً وارداً (incoming webhook) للقناة التي يجيب فيها الوكيل، وانسخ عنوانه.",
      mm3: "اضغط التالي والصق القيمتين.",
      bridgeIntro: "يتصل {{app}} عبر برنامج جسر صغير يشغّله فريق تقنية المعلومات داخل شبكتكم. يتحقق من أمان {{app}} وينقل الرسائل من المنصة وإليها.",
      br1: "أعطِ فريق تقنية المعلومات العنوان أدناه: يرسل الجسر الرسائل الواردة إليه.",
      br2: "أعطهم السر المشترك من الخطوة التالية: يوقّع الجسر كل رسالة به.",
      br3: "اطلب منهم عنوان الإرسال الخاص بالجسر (حيث ترسل المنصة الردود).",
      inboundLabel: "عنوان الرسائل الواردة",
      copyInbound: "نسخ عنوان الرسائل الواردة",
      inboundHelp: "يحتاج هذا العنوان الجسر أو خادم المحادثة فقط.",
      displayName: "الاسم",
      displayNameHelp: "كيف يظهر هذا الربط هنا، مثلاً «واتساب الدعم».",
      agent: "الوكيل الذي يجيب",
      agentPlaceholder: "اختر وكيلاً",
      mmToken: "رمز الخطاف الصادر",
      mmTokenHelp: "يعرضه ماترموست عند إنشاء الخطاف الصادر.",
      sharedSecret: "السر المشترك",
      sharedSecretHelp: "أنشأناه لك. انسخه الآن وأعطه لفريق تقنية المعلومات: لن يُعرض بعد الحفظ.",
      keepSecret: "اتركه فارغاً للإبقاء على القيمة الحالية.",
      generate: "إنشاء",
      copySecret: "نسخ السر",
      mmWebhook: "عنوان الخطاف الوارد",
      mmWebhookHelp: "العنوان الذي يعرضه ماترموست للخطاف الوارد.",
      endpoint: "عنوان إرسال الجسر",
      endpointHelp: "حيث ترسل المنصة ردود {{app}}. يعطيك إياه فريق تقنية المعلومات.",
      technical: "الاسم الداخلي: {{name}} · مسار الوارد: {{path}}",
      saveConnect: "حفظ وربط",
      saved: "تم ربط {{name}}",
      done: "تم ربط {{name}}",
      doneHelp: "أرسل رسالة تجريبية للتأكد من وصول الردود.",
      testTitle: "إرسال رسالة تجريبية",
      testFor: "تجربة {{name}}",
      conversation: "المحادثة أو القناة التي تُرسل إليها التجربة",
      conversationHelp: "معرّف محادثة في {{app}} تستطيع رؤيتها، مثل محادثتك مع البوت.",
      mmTestHelp: "تذهب الرسالة التجريبية إلى قناة الخطاف الوارد.",
      testLanguage: "لغة الرسالة التجريبية",
      sendTest: "إرسال التجربة",
      testSent: "تم إرسال الرسالة التجريبية",
      testSentHelp: "افحص المحادثة: يجب أن تجد الرسالة هناك.",
      testNotSent: "لم تُرسل الرسالة التجريبية",
      reason: {
        no_outbound: "لا يوجد عنوان لإرسال الردود. أضفه عبر «تغيير».",
        conversation_required: "أدخل المحادثة أو القناة التي تُرسل إليها التجربة.",
        not_found: "هذا الربط لم يعد موجوداً.",
        disabled: "الربط متوقف. شغّله أولاً.",
        secret_missing: "تعذر العثور على عنوان الخطاف المحفوظ. أدخله مجدداً عبر «تغيير».",
        upstream_failed: "لم يقبل تطبيق المحادثة أو الجسر الرسالة. تحقق من عنوان الإرسال ومن أن الجسر يعمل.",
      },
      emailTitle: "ربط البريد الإلكتروني",
      emailChangeTitle: "تغيير ربط البريد الإلكتروني",
      emailConfigured: "البريد الإلكتروني متصل. حفظ سر جديد يستبدل القديم، فحدّث خادم البريد أيضاً.",
      emailIntro: "يعيد خادم بريدكم توجيه الرسائل الواصلة إلى بريد الدعم إلى المنصة، موقّعة بسر مشترك. وتُرسل الردود عبر خدمة البريد في المنصة.",
      email1: "في خادم البريد، أعد توجيه الرسائل الواردة إلى هذا العنوان:",
      email2: "استخدم هذا السر المشترك لتوقيعها (انسخه الآن، لن يُعرض مرة أخرى):",
      email3: "احفظ هنا، ثم أرسل رسالة تجريبية إلى بريد الدعم.",
      emailNote1: "يتم تجاهل الردود التلقائية والقوائم البريدية.",
      emailNote2: "تُعامل الرسائل كمدخلات غير موثوقة: لا ينفذ الوكيل أي تعليمات مكتوبة فيها.",
      emailConfirm: "نسخت السر إلى خادم البريد",
      emailSave: "حفظ ربط البريد الإلكتروني",
      emailSaved: "تم ربط البريد الإلكتروني",
    },
    notify: {
      intro: "أرسل رسالة واحدة إلى عميل. لا تُرسل إلا إذا وافق العميل على هذا النوع من الرسائل، وخارج ساعات الهدوء وضمن الحدود.",
      how: "الإرسال عبر",
      via: { email: "البريد الإلكتروني", chat: "تطبيق محادثة" },
      connector: "تطبيق المحادثة",
      noConnector: "لا يوجد تطبيق محادثة متصل",
      toEmail: "بريد العميل الإلكتروني",
      toChat: "معرّف العميل في المحادثة",
      purpose: "نوع الرسالة",
      purposeHelp: "يجب أن يكون العميل قد وافق على هذا النوع من الرسائل.",
      message: "الرسالة",
      templates: { appointment_reminder: "تذكير بموعد", case_update: "تحديث على طلب", generic_notice: "إشعار بنص حر", payment_due: "تذكير بالدفع" },
      params: { date: "التاريخ", reference: "الرقم المرجعي", time: "الوقت", status: "الحالة الجديدة", amount: "المبلغ", currency: "العملة", message: "رسالتك" },
      language: "لغة الرسالة",
      send: "إرسال",
      previewTitle: "ما سيقرؤه العميل",
      previewEmpty: "اختر رسالة لتظهر هنا.",
      status: { sent: "أُرسلت إلى {{to}}", deferred: "ستُرسل إلى {{to}} لاحقاً", blocked: "لم تُرسل إلى {{to}}" },
      when: "موعد الإرسال: {{at}}.",
      reason: {
        no_consent: "لم يوافق العميل على هذا النوع من الرسائل. سجّل موافقته أولاً (تبويب الموافقات).",
        opted_out: "طلب العميل عدم التواصل معه عبر هذه القناة.",
        quiet_hours: "الوقت ضمن ساعات الهدوء أو عطلة نهاية الأسبوع؛ ستُرسل عند انتهائها.",
        rate_limited: "أُرسلت رسائل كثيرة مؤخراً؛ ستُرسل خلال ساعة.",
        not_allowed: "لا يسمح حسابك بإرسال الإشعارات.",
      },
    },
    settings: {
      intro: "تنطبق هذه الإعدادات على كل قنوات مؤسستك. يغيّرها مسؤولو القنوات فقط.",
      readOnly: "يمكنك قراءة هذه الإعدادات. تغييرها يتطلب دور مسؤول القنوات.",
      hoursTitle: "أوقات العمل",
      useOrg: "استخدام المنطقة الزمنية وعطلة نهاية الأسبوع الخاصة بالمؤسسة",
      useOrgOn: "يتبع المؤسسة: {{zone}}.",
      useOrgNone: "لم تحدد المؤسسة منطقة زمنية بعد؛ تُستخدم قيم القناة.",
      useOrgOff: "لهذه القناة منطقتها الزمنية وعطلتها الخاصة.",
      timezone: "المنطقة الزمنية",
      quietStart: "بداية ساعات الهدوء",
      quietEnd: "نهاية ساعات الهدوء",
      quietHelp: "لا تُرسل إشعارات بين هذين الوقتين.",
      weekendDays: "عطلة نهاية الأسبوع (بلا إشعارات)",
      weekendOwn: "لا تحدد المؤسسة عطلة نهاية الأسبوع؛ اخترها هنا.",
      days: { "1": "الاثنين", "2": "الثلاثاء", "3": "الأربعاء", "4": "الخميس", "5": "الجمعة", "6": "السبت", "7": "الأحد" },
      peopleTitle: "عندما يطلب العميل موظفاً",
      queueRoles: "من يجيب",
      queueHelp: "يرى أصحاب أي من هذه الأدوار المحادثات في مكتب الموظفين.",
      sla: "الرد خلال",
      slaHelp: "بعد هذه المدة يظهر الطلب متأخراً.",
      minutes: "دقيقة",
      defaultAgent: "الوكيل الذي يجيب افتراضياً",
      noAgent: "لا يوجد",
      messagesTitle: "الرسائل",
      defaultLanguage: "اللغة الافتراضية",
      digits: "الأرقام في الرسائل",
      digitsLatin: "0 1 2 3",
      digitsArabic: "٠ ١ ٢ ٣",
      perHour: "أقصى عدد للإشعارات في الساعة",
      perRecipient: "أقصى عدد للإشعارات لكل عميل في اليوم",
      optOut: "كلمات توقف الرسائل",
      optOutHelp: "العميل الذي يرسل إحدى هذه الكلمات لا يُتواصل معه مجدداً عبر تلك القناة. كلمتا STOP وإيقاف تعملان دائماً.",
      purposesTitle: "أنواع الرسائل التي يمكن للعملاء الموافقة عليها",
      purposesHelp: "تُستخدم عند تسجيل موافقة العميل وعند إرسال إشعار.",
      purposeAr: "نوع الرسالة {{n}} بالعربية",
      purposeEn: "نوع الرسالة {{n}} بالإنجليزية",
      purposeArPh: "بالعربية",
      purposeEnPh: "In English",
      removePurpose: "إزالة {{name}}",
      newPurposeAr: "نوع رسالة جديد بالعربية",
      newPurposeEn: "نوع رسالة جديد بالإنجليزية",
      addPurpose: "إضافة",
      save: "حفظ الإعدادات",
      saved: "تم حفظ إعدادات القنوات",
    },
    consents: {
      intro: "يجب أن يوافق العميل قبل أن ترسل إليه رسائل. ابحث عنه بطريقة التواصل معه؛ تُحفظ العناوين مشفّرة وتظهر هنا مخفية جزئياً.",
      recipient: "العميل",
      purposeHelp: "تأتي أنواع الرسائل من إعدادات القنوات.",
    },
    roleNames: {
      approver: "المعتمِدون",
      reviewer: "المراجِعون",
      "case-worker": "موظفو الحالات",
      "case-supervisor": "مشرفو الحالات",
      finance: "فريق المالية",
      "finance-approver": "معتمدو المالية",
      builder: "بناة الوكلاء",
      "tool-owner": "مالكو الأدوات",
      "retrieval-curator": "أمناء المعرفة",
      "eval-curator": "أمناء التقييم",
      "memory-curator": "أمناء الذاكرة",
      "data-steward": "أمناء البيانات",
      "release-approver": "معتمدو الإصدارات",
      "agent-admin": "مسؤولو الوكلاء",
      "model-admin": "مسؤولو النماذج",
      "guardrails-admin": "مسؤولو الضوابط الوقائية",
      "case-admin": "مسؤولو الحالات",
      "privacy-officer": "مسؤولو الخصوصية",
      "pii-reader": "قرّاء البيانات الشخصية",
      "kill-switch-operator": "مشغلو الإيقاف الطارئ",
      "tenant-admin": "مسؤولو المؤسسة",
      auditor: "المدققون",
      operator: "المشغّلون",
      executive: "التنفيذيون",
      "channel-agent": "موظفو الدعم",
      "channel-supervisor": "مشرفو الدعم",
      "channel-admin": "مسؤولو القنوات",
      "channel-consent-admin": "مسؤولو الموافقات",
      "channel-campaigner": "مرسلو الإشعارات",
    },
    issues: {
      name_required: "أدخل اسماً",
      origins_required: "أضف موقعاً واحداً على الأقل",
      agent_required: "اختر وكيلاً",
      secret_short: "يجب أن يكون السر 8 أحرف على الأقل",
      webhook_required: "أدخل عنوان الخطاف الوارد",
      endpoint_required: "أدخل عنوان إرسال الجسر",
      url_invalid: "تبدأ العناوين بـ https:// أو http://",
      email_secret_short: "يجب أن يكون السر 16 حرفاً على الأقل",
      address_short: "أدخل عنوان العميل",
      connector_required: "اختر تطبيق المحادثة",
      purpose_required: "اختر نوع الرسالة",
      template_required: "اختر رسالة",
      fields_required: "املأ كل حقول الرسالة",
      time_invalid: "اختر وقتي ساعات الهدوء",
      queue_required: "اختر من يجيب",
      purpose_label_required: "يحتاج كل نوع رسالة إلى اسم بالعربية وبالإنجليزية",
      purpose_invalid: "لأحد أنواع الرسائل مفتاح داخلي غير صالح",
      purpose_duplicate: "لنوعين من الرسائل الاسم نفسه",
    },
    // Stream C (ADR-0101): website visitors get answers; the widget follows the released version.
    visitors: {
      ok: "يحصل الزوار على إجابات.",
      unknown: "تعذّر التحقق مما إذا كان الزوار يحصلون على إجابات.",
      title: "لا يحصل الزوار على إجابة بعد",
      intro: "إلى أن يُصلح هذا، يُبلَّغ الزوار بأن المساعد لا يستطيع الرد بعد.",
      problem: {
        not_found: "الوكيل المرتبط لم يعد موجودًا. اختر وكيلاً آخر.",
        not_released: "لم يُصدر أي إصدار من الوكيل بعد.",
        no_trigger_key: "ليس لدى الوكيل مفتاح للمحادثات دون شخص مسجّل الدخول.",
        not_autonomous: "لا يُسمح للوكيل بالعمل وحده، وهذا لازم عندما لا يكون أحد مسجّل الدخول.",
        channel_off: "الوكيل لا يجيب على المواقع الإلكترونية.",
        tier_too_high: "مستوى خطورة الوكيل أعلى من أن يعمل دون شخص مسجّل الدخول. يجب على من يبني الوكلاء خفضه أولاً.",
      },
      allow: "السماح لزوار الموقع",
      allowAria: "السماح لزوار الموقع في {{name}}",
      confirmTitle: "السماح لزوار الموقع؟",
      confirmIntro: "يُعِدّ هذا {{agent}} ليحصل زوار هذه المحادثة على إجابات:",
      step: {
        key: "إنشاء المفتاح للمحادثات دون شخص مسجّل الدخول.",
        version: "حفظ إصدار جديد يعمل وحده ويجيب على المواقع الإلكترونية.",
        release: "إصداره إلى بيئة التطوير.",
        follow: "جعل محادثة الموقع هذه تتبع الإصدار المنشور.",
      },
      confirmNote: "يُسجَّل كل تغيير في سجل النشاط.",
      confirmOk: "السماح للزوار",
      done: "أصبح زوار {{name}} يحصلون على إجابات.",
      waiting: "تم الحفظ. الإصدار بانتظار الموافقة في الحوكمة.",
      failed: "تعذّر الإكمال: {{message}}",
      askBuilder: "اطلب ممن يبني الوكلاء السماح لزوار الموقع لهذا الوكيل (المنشئ › متى يعمل).",
      follows: "يتبع الإصدار المنشور",
      followsNow: "يجيب بالإصدار المنشور (حاليًا الإصدار {{version}}).",
      keeps: "يُبقي الإصدار {{version}}",
      keepSwitch: "إبقاء إصدار واحد",
      keepHelp: "عادةً تجيب المحادثة بكل إصدار جديد. أبقِ إصدارًا واحدًا فقط لتأخير التحديث.",
    },
  },
};
